import { useState, useRef, useEffect } from "react";
import { supabase } from "../hooks/useData.js";
import { useTheme } from "../lib/ThemeContext.jsx";
import {FONT} from "../lib/theme.js";

const SUGGESTIONS = [
  "What's my cashflow for next month?",
  "When is my next payment due?",
  "How much runway do I have?",
  "What if my main product is out of stock from 3rd May for 2 weeks?",
  "Can I afford a new order of €50,000?",
  "What are my biggest expenses this month?",
];

function TypingIndicator({ T }) {
  return (
    <div style={{ display:"flex", gap:4, alignItems:"center", padding:"12px 16px" }}>
      {[0,1,2].map(i => (
        <div key={i} style={{
          width:7, height:7, borderRadius:"50%",
          background: T.blue,
          animation: `chatBounce 1.2s ${i*0.2}s ease-in-out infinite`,
        }}/>
      ))}
    </div>
  );
}

function Message({ msg, T }) {
  const isUser = msg.role === "user";
  return (
    <div style={{
      display:"flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom:12,
      animation:"chatFadeUp 0.2s ease both",
    }}>
      {!isUser && (
        <div style={{
          width:28, height:28, borderRadius:8, flexShrink:0,
          background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:11, fontWeight:800, color:"#fff", marginRight:8, marginTop:2,
        }}>CH</div>
      )}
      <div style={{
        maxWidth:"80%",
        background: isUser ? T.blue : T.surface,
        color: isUser ? "#fff" : T.text,
        borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
        padding:"11px 15px",
        fontSize:14, lineHeight:1.55,
        border: isUser ? "none" : `1px solid ${T.border}`,
        whiteSpace:"pre-wrap",
        wordBreak:"break-word",
      }}>
        {msg.content}
        {msg.simulation && (
          <div style={{
            marginTop:8, padding:"6px 10px",
            background:"rgba(255,159,10,0.15)",
            border:"1px solid rgba(255,159,10,0.3)",
            borderRadius:8, fontSize:12, color:"#ff9f0a",
          }}>
            ⚡ Simulation: {msg.simulation.description}
          </div>
        )}
        {msg.pendingAction && (
          <div style={{
            marginTop:8, padding:"6px 10px",
            background:"rgba(255,193,7,0.15)",
            border:"1px solid rgba(255,193,7,0.4)",
            borderRadius:8, fontSize:12, color:"#ffc107",
            display:"flex", alignItems:"center", gap:6,
          }}>
            <span style={{ animation:"chatBounce 1.4s infinite" }}>⏳</span>
            <span><strong>Awaiting confirmation</strong> — reply <code>YES</code> to proceed or <code>NO</code> to cancel.</span>
          </div>
        )}
        {msg.toolsUsed && msg.toolsUsed.length > 0 && (
          <div style={{
            marginTop:6, fontSize:10, color:"#8b5cf6", opacity:0.8,
          }}>
            🔧 Used: {msg.toolsUsed.join(", ")}
          </div>
        )}
        {msg.quotaExceeded && (
          <div style={{
            marginTop:10, padding:"10px 12px",
            background:"rgba(139,92,246,0.10)",
            border:"1px solid rgba(139,92,246,0.35)",
            borderRadius:10, fontSize:12, color:T.text,
          }}>
            <div style={{ fontWeight:700, marginBottom:4 }}>
              {msg.quotaUsed ?? "?"} / {msg.quotaLimit ?? "?"} messages used this month
            </div>
            <div style={{ color:T.textSub, marginBottom:10, lineHeight:1.5 }}>
              Upgrade your plan to keep the conversation going. Your quota resets on the 1st.
            </div>
            <button
              onClick={() => { window.location.hash = "profile"; }}
              style={{
                background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
                color:"#fff", border:"none", borderRadius:8,
                padding:"7px 16px", fontSize:12, fontWeight:700,
                cursor:"pointer",
              }}
            >Upgrade plan →</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChatWidget({ userId, brandKey = "main" }) {
  // Resolve session token for authenticated API calls
  const [accessToken, setAccessToken] = useState(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAccessToken(session?.access_token || null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, s) => {
      setAccessToken(s?.access_token || null);
    });
    return () => subscription.unsubscribe();
  }, []);
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{
    role:"assistant",
    content:"Hi! I'm your CashHorizon AI. Ask me anything about your cashflow — or try a what-if simulation like \"What if my product is out of stock for 2 weeks from May 3rd?\"",
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior:"smooth" });
    }
  }, [messages, loading]);

  async function sendMessage(text) {
    const userMsg = text || input.trim();
    if (!userMsg || loading) return;
    setInput("");

    const newMessages = [...messages, { role:"user", content:userMsg }];
    setMessages(newMessages);
    setLoading(true);

    // Add placeholder for assistant response
    setMessages(m => [...m, { role:"assistant", content:"", streaming:true }]);

    try {
      const res = await fetch("/api/chat", {
        method:"POST",
        headers:{ "Content-Type":"application/json", ...(accessToken ? { "Authorization": `Bearer ${accessToken}` } : {}) },
        body: JSON.stringify({
          userId,
          brandKey,
          messages: newMessages.map(m => ({ role:m.role, content:m.content })),
        }),
      });

      // Parse JSON body regardless of status so we can surface structured
      // errors (e.g. 402 quota-exceeded with upgrade context) instead of
      // collapsing everything into "something went wrong".
      let data = null;
      try { data = await res.json(); } catch {}

      if (!res.ok) {
        // 402 → quota exhausted. The server returns:
        //   { error, quotaExceeded: true, used, limit, tier }
        // Render a dedicated assistant bubble with the friendly copy +
        // an upgrade CTA. Keep the message in the chat history so the
        // user has a clear explanation rather than a disappeared turn.
        if (res.status === 402 && data?.quotaExceeded) {
          setMessages(m => m.map((msg, i) =>
            i === m.length - 1
              ? {
                  ...msg,
                  content: data.error || "You've used your AI messages for this month.",
                  streaming: false,
                  quotaExceeded: true,
                  quotaUsed: data.used,
                  quotaLimit: data.limit,
                  quotaTier: data.tier,
                }
              : msg
          ));
          return;
        }
        throw new Error(data?.error || "Chat request failed");
      }

      if (data?.error) throw new Error(data.error);

      setMessages(m => m.map((msg, i) =>
        i === m.length - 1
          ? {
              ...msg,
              content: data.text,
              streaming: false,
              simulation: data.simulation || null,
              pendingAction: !!data.pending_action,
              toolsUsed: data.tools_used || [],
            }
          : msg
      ));

      if (!open) setUnread(u => u + 1);
    } catch (err) {
      setMessages(m => m.map((msg, i) =>
        i === m.length - 1
          ? { ...msg, content:"Sorry, something went wrong. Please try again.", streaming:false }
          : msg
      ));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{`
        @keyframes chatBounce {
          0%,80%,100%{transform:translateY(0)}
          40%{transform:translateY(-6px)}
        }
        @keyframes chatFadeUp {
          from{opacity:0;transform:translateY(8px)}
          to{opacity:1;transform:translateY(0)}
        }
        @keyframes chatSlideUp {
          from{opacity:0;transform:translateY(20px) scale(0.96)}
          to{opacity:1;transform:translateY(0) scale(1)}
        }
        @keyframes chatSpin {
          from{transform:rotate(0deg)} to{transform:rotate(360deg)}
        }
        .chat-input:focus { outline:none; border-color: #8b5cf6 !important; }
        .chat-suggestion:hover { background: rgba(139,92,246,0.12) !important; color: #8b5cf6 !important; }
        .chat-send:hover { background: #005bbf !important; }
        .chat-send:disabled { opacity:0.5; cursor:not-allowed; }
      `}</style>

      {/* ── Floating button — identical to loading screen logo ── */}
      <button
        data-tour="chat-widget"
        onClick={() => setOpen(o => !o)}
        style={{
          position:"fixed", bottom:"max(24px, calc(env(safe-area-inset-bottom) + 16px))", right:16, zIndex:9000,
          width:56, height:56, borderRadius:"50%",
          background:"transparent",
          border:"none", cursor:"pointer", padding:0,
          display:"flex", alignItems:"center", justifyContent:"center",
          transition:"transform 0.2s",
          transform: open ? "scale(0.9)" : "scale(1)",
        }}
        onMouseEnter={e => e.currentTarget.style.transform="scale(1.08)"}
        onMouseLeave={e => e.currentTarget.style.transform=open?"scale(0.9)":"scale(1)"}
      >
        {open ? (
          /* Close button — keep same visual footprint */
          <div style={{ width:56, height:56, borderRadius:14, background:"linear-gradient(135deg,#8b5cf6,#06b6d4)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 8px 32px rgba(139,92,246,0.5)" }}>
            <span style={{ fontSize:20, color:"#fff", fontWeight:300, lineHeight:1 }}>✕</span>
          </div>
        ) : (
          <div style={{ position:"relative", width:56, height:56, display:"flex", alignItems:"center", justifyContent:"center" }}>
            {/* Gradient square — identical to loading screen, scaled to 50px */}
            <div style={{
              width:50, height:50, borderRadius:14,
              background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
              boxShadow:"0 8px 32px rgba(139,92,246,0.5)",
              display:"flex", alignItems:"center", justifyContent:"center",
              position:"relative", zIndex:1,
            }}>
              <span style={{ fontSize:10, fontWeight:800, color:"#fff", letterSpacing:-0.3, textAlign:"center", lineHeight:1.2 }}>Ask<br/>me</span>
            </div>
            {/* Inner ring — identical to loading screen (inset:-4, borderRadius:22 → scaled) */}
            <div style={{
              position:"absolute", inset:-3, borderRadius:17,
              border:"2.5px solid transparent",
              borderTopColor:"#8b5cf6",
              borderRightColor:"#06b6d4",
              animation:"chatSpin 1.2s linear infinite",
            }}/>
            {/* Outer faint ring — reverse, identical to loading screen */}
            <div style={{
              position:"absolute", inset:-7, borderRadius:21,
              border:"1.5px solid transparent",
              borderBottomColor:"#8b5cf6",
              borderLeftColor:"#06b6d4",
              opacity:0.35,
              animation:"chatSpin 2.4s linear infinite reverse",
            }}/>
          </div>
        )}
        {unread > 0 && !open && (
          <div style={{
            position:"absolute", top:0, right:0,
            width:18, height:18, borderRadius:"50%",
            background:"#ff3b30", color:"#fff",
            fontSize:10, fontWeight:700,
            display:"flex", alignItems:"center", justifyContent:"center",
            border:`2px solid ${T.bg}`,
          }}>{unread}</div>
        )}
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div style={{
          position:"fixed", bottom:"max(92px, calc(env(safe-area-inset-bottom) + 84px))", right:16, zIndex:9000,
          // Clamp to viewport on narrow phones so the panel doesn't spill
          // off-screen (hard-coded 380 broke on 360px Android).
          width:"min(380px, calc(100vw - 32px))",
          height:"min(560px, calc(100dvh - 140px))",
          background:T.card,
          border:`1px solid ${T.border2 || T.border}`,
          borderRadius:20,
          boxShadow:"0 24px 72px rgba(0,0,0,0.6)",
          display:"flex", flexDirection:"column",
          overflow:"hidden",
          animation:"chatSlideUp 0.25s cubic-bezier(0.34,1.56,0.64,1) both",
          fontFamily:FONT,
        }}>

          {/* Header */}
          <div style={{
            padding:"14px 18px",
            background:"linear-gradient(135deg,rgba(0,113,227,0.15),rgba(0,198,255,0.08))",
            borderBottom:`1px solid ${T.border}`,
            display:"flex", alignItems:"center", gap:10,
          }}>
            <div style={{
              width:32, height:32, borderRadius:9,
              background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:12, fontWeight:800, color:"#fff",
            }}>CH</div>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:T.text }}>CashHorizon AI</div>
              <div style={{ fontSize:11, color:T.green, display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:T.green, display:"inline-block" }}/>
                Live data · Simulation ready
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{
              marginLeft:"auto", background:"none", border:"none",
              color:T.textSub, cursor:"pointer", fontSize:18, padding:"0 4px",
              lineHeight:1,
            }}>×</button>
          </div>

          {/* Messages */}
          <div style={{
            flex:1, overflowY:"auto", padding:"16px 14px",
            display:"flex", flexDirection:"column",
          }}>
            {messages.map((msg, i) => (
              <Message key={i} msg={msg} T={T}/>
            ))}
            {loading && messages[messages.length-1]?.streaming === false && (
              <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
                <div style={{ width:28,height:28,borderRadius:8,background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",flexShrink:0 }}>CH</div>
                <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:"18px 18px 18px 4px",padding:"4px 8px" }}>
                  <TypingIndicator T={T}/>
                </div>
              </div>
            )}

            {/* Suggestions — only show at start */}
            {messages.length === 1 && (
              <div style={{ marginTop:8 }}>
                <div style={{ fontSize:11, color:T.textMuted, marginBottom:8, fontWeight:600, letterSpacing:0.3, textTransform:"uppercase" }}>Try asking</div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {SUGGESTIONS.slice(0,4).map((s, i) => (
                    <button key={i} className="chat-suggestion" onClick={() => sendMessage(s)}
                      style={{
                        background:"transparent", border:`1px solid ${T.border}`,
                        borderRadius:12, padding:"8px 12px",
                        fontSize:12, color:T.textSub, cursor:"pointer",
                        textAlign:"left", fontFamily:FONT,
                        transition:"all 0.15s",
                      }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* Input */}
          <div style={{
            padding:"12px 14px",
            borderTop:`1px solid ${T.border}`,
            background:T.card,
            display:"flex", gap:8, alignItems:"flex-end",
          }}>
            <textarea
              ref={inputRef}
              className="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask about your cashflow…"
              rows={1}
              style={{
                flex:1, background:T.surface,
                border:`1.5px solid ${T.border}`,
                borderRadius:12, padding:"10px 13px",
                fontSize:14, color:T.text,
                fontFamily:FONT, resize:"none",
                lineHeight:1.4, maxHeight:100,
                overflowY:"auto",
              }}
              onInput={e => {
                e.target.style.height="auto";
                e.target.style.height=Math.min(e.target.scrollHeight,100)+"px";
              }}
            />
            <button
              className="chat-send"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{
                width:38, height:38, borderRadius:12,
                background:T.blue, border:"none",
                color:"#fff", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:16, flexShrink:0,
                transition:"background 0.15s",
              }}
            >↑</button>
          </div>
        </div>
      )}
    </>
  );
}
